/**
 * useAuditTrail Hook
 * Fetches the audit trail for a finding
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { AuditEntry } from '../types/api';

export const auditTrailKeys = {
  all: ['audit-trail'] as const,
  byResource: (type: string, id: string) => [...auditTrailKeys.all, type, id] as const,
};

/**
 * Fetch audit trail entries for a finding
 */
export function useAuditTrail(resourceType: string, resourceId: string | undefined) {
  return useQuery({
    queryKey: auditTrailKeys.byResource(resourceType, resourceId!),
    queryFn: () => api.findings.getAuditTrail(resourceId!),
    enabled: Boolean(resourceId),
    select: (data) => data.entries,
  });
}

/**
 * Format an audit action for display
 */
export function formatAuditAction(entry: AuditEntry): string {
  const actions: Record<string, string> = {
    'finding.create': 'Finding created',
    'finding.update': 'Finding updated',
    'finding.close': 'Finding closed and verified',
    'finding.delete': 'Finding deleted',
    'exception.grant': 'Exception granted',
    'exception.update': 'Exception updated',
    'exception.revoke': 'Exception revoked',
    'exception.expire': 'Exception expired',
    'evidence.upload': 'Evidence uploaded',
    'status.change': 'Status changed',
    'subjectivity.create': 'Subjectivity created',
    'subjectivity.update': 'Subjectivity updated',
    'subjectivity.waive': 'Subjectivity waived',
    'subjectivity.expire': 'Subjectivity expired',
    'subjectivity.cancel': 'Subjectivity cancelled',
  };
  return actions[entry.action] || entry.action;
}

/**
 * Get icon name for audit action (used by AuditTrail component)
 */
export function getAuditActionIcon(action: string): 'clipboard' | 'shield' | 'camera' | 'check' | 'x' | 'clock' {
  const icons: Record<string, 'clipboard' | 'shield' | 'camera' | 'check' | 'x' | 'clock'> = {
    'finding.create': 'clipboard',
    'finding.update': 'clipboard',
    'finding.close': 'check',
    'finding.delete': 'x',
    'exception.grant': 'shield',
    'exception.update': 'shield',
    'exception.revoke': 'x',
    'exception.expire': 'clock',
    'evidence.upload': 'camera',
    'status.change': 'clock',
    'subjectivity.create': 'clipboard',
    'subjectivity.update': 'clipboard',
    'subjectivity.waive': 'check',
    'subjectivity.expire': 'clock',
    'subjectivity.cancel': 'x',
  };
  return icons[action] || 'clipboard';
}

export default useAuditTrail;
